import { getToken } from "./auth"

const API_BASE_URL = process.env.REACT_APP_API_URL
// const API_BASE_URL = "http://localhost:3001/api"

/**
 * Get authorization headers with token
 * @returns {Object} Headers object with Authorization
 */
const getAuthHeaders = () => {
  const token = getToken()
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }
}

const handleResponse = async (response) => {
  const data = await response.json()

  if (!response.ok || data.success === false) {
    throw new Error(data.message || "API request failed")
  }

  return data
}

/**
 * Get all companies (with optional status filter)
 * @param {number} page - Page number (default 1)
 * @param {number} limit - Items per page (default 10)
 * @param {string} status - Optional status filter: "pending" | "approved" | "rejected" | ""
 * @returns {Promise<Object>} - Response with companies data and pagination
 */
export const getCompanies = async (page = 1, limit = 10, status = "") => {
  const url = new URL(`${API_BASE_URL}/admin/companies`)

  url.searchParams.append("page", page)
  url.searchParams.append("limit", limit)
  if (status) {
    url.searchParams.append("status", status)
  }

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: getAuthHeaders(),
  })

  return handleResponse(response)
}

/**
 * Verify a company (approve)
 * @param {string} companyId - Company ID to verify
 * @returns {Promise<Object>} - Response with updated company data
 */
export const verifyCompany = async (companyId) => {
  const response = await fetch(`${API_BASE_URL}/admin/companies/${companyId}/verify`, {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify({}),
  })

  return handleResponse(response)
}

/**
 * Reject a company
 * @param {string} companyId - Company ID to reject
 * @param {string} reason - Rejection reason (optional)
 * @returns {Promise<Object>} - Response with updated company data
 */
export const rejectCompany = async (companyId, rejectionReason = "") => {
  const response = await fetch(`${API_BASE_URL}/admin/companies/${companyId}/reject`, {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify({ rejectionReason }),
  })

  return handleResponse(response)
}

/**
 * Add a new company (admin)
 * @param {Object} companyData - Company data object
 * @param {File} logoFile - Logo file (optional)
 * @returns {Promise<Object>} - Response with created company data
 */
export const addCompany = async (companyData, logoFile = null) => {
  const token = getToken()
  const formData = new FormData()

  // Add all text fields
  Object.entries(companyData).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      // Arrays need to be stringified (applicableTaxes, operatingPorts, operatingCountries)
      if (Array.isArray(value)) {
        formData.append(key, JSON.stringify(value))
      } else {
        formData.append(key, value)
      }
    }
  })

  // Add logo file if provided
  if (logoFile) {
    formData.append("logo", logoFile)
  }

  const response = await fetch(`${API_BASE_URL}/companies/admin/add`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      // Note: Don't set Content-Type for FormData, browser will set it with boundary
    },
    body: formData,
  })

  return handleResponse(response)
}

/**
 * Toggle company active status (enable/disable)
 * @param {string} companyId - Company ID to toggle
 * @param {boolean} isActive - New active status
 * @returns {Promise<Object>} - Response with updated company data
 */
export const toggleCompanyStatus = async (companyId, isActive) => {
  const response = await fetch(`${API_BASE_URL}/admin/companies/${companyId}/toggle-status`, {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify({ isActive }),
  })

  return handleResponse(response)
}
